import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import type { OrgDeptNode, OrgUser, QuickCreateRecurrence } from "../api/types";
import { useToast } from "./Toast";

/**
 * 一键建会弹窗（管理员）：
 * 左栏组织架构树 → 点部门展开/收起该部门人员（同时设为会议归属部门），
 * 点人员即勾选/取消为参会人（跨部门勾选保留）；
 * 右栏配置标题、主持人（从已选参会人中指定）、周期（每个工作日 / 每周多选 / 每月多选）。
 * 周期规则与后端 domain/recurrence.ts 保持一致：
 *   · 企微一场系列会议只能同一周期日重复 → 每周一+周三会创建两个会议系列
 *   · 结束方式「按次数」由前端换算结束日期（工作日/每周 ≤200 场，每月 ≤50 场）
 */

const WEEKDAY_CN = ["日", "一", "二", "三", "四", "五", "六"];
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 7]; // 周一 → 周日
const DURATION_OPTIONS = [30, 45, 60, 90, 120];

interface QuickCreateModalProps {
  open: boolean;
  onClose: () => void;
  /** 创建成功后的回调（刷新监测数据） */
  onCreated: () => void;
}

function tomorrowKey(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** 与后端一致的系列数量预览 */
function planLabels(recurrence: QuickCreateRecurrence): string[] {
  if (recurrence.type === "WEEKLY") {
    return [...(recurrence.weekdays ?? [])]
      .sort((a, b) => a - b)
      .map((w) => `每周${WEEKDAY_CN[w % 7]}`);
  }
  if (recurrence.type === "MONTHLY") {
    return [...(recurrence.monthDays ?? [])]
      .sort((a, b) => a - b)
      .map((d) => `每月 ${d} 日`);
  }
  return ["每个工作日"];
}

export function QuickCreateModal({ open, onClose, onCreated }: QuickCreateModalProps) {
  const { show } = useToast();

  /* ---- 组织架构 ---- */
  const [depts, setDepts] = useState<OrgDeptNode[]>([]);
  const [deptsLoading, setDeptsLoading] = useState(false);
  const [deptFilter, setDeptFilter] = useState("");
  /** 会议归属部门（点击部门行时设置） */
  const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null);
  /** 展开的部门（显示其下人员） */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  /** 已加载的部门成员缓存：deptId → 成员列表 */
  const [deptUsers, setDeptUsers] = useState<Map<string, OrgUser[]>>(new Map());
  const [loadingDeptIds, setLoadingDeptIds] = useState<Set<string>>(new Set());
  /** 已选参会人（跨部门保留） */
  const [selected, setSelected] = useState<Set<string>>(new Set());

  /* ---- 表单 ---- */
  const [title, setTitle] = useState("");
  const [type, setType] = useState<QuickCreateRecurrence["type"]>("WEEKLY");
  const [weekdays, setWeekdays] = useState<number[]>([1]);
  const [monthDays, setMonthDays] = useState<number[]>([1]);
  const [startDate, setStartDate] = useState(tomorrowKey);
  const [timeOfDay, setTimeOfDay] = useState("09:00");
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [endType, setEndType] = useState<"count" | "date">("count");
  const [endCount, setEndCount] = useState(12);
  const [endDate, setEndDate] = useState("");
  /** 会议主持人（从已选参会人中单选，可空） */
  const [hostUserid, setHostUserid] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ---- userid → 姓名映射（来自已加载的各部门成员） ---- */
  const nameOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const list of deptUsers.values()) {
      for (const u of list) map.set(u.userid, u.name);
    }
    return map;
  }, [deptUsers]);

  /* ---- 已选参会人明细（保持勾选顺序） ---- */
  const selectedUsers = useMemo(
    () =>
      [...selected].map((userid) => ({ userid, name: nameOf.get(userid) ?? userid })),
    [selected, nameOf],
  );

  /* ---- 加载部门树 ---- */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setDeptsLoading(true);
    api.org
      .departments()
      .then((list) => {
        if (!cancelled) setDepts(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "组织架构加载失败");
      })
      .finally(() => {
        if (!cancelled) setDeptsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  /* ---- 加载某部门成员（带缓存） ---- */
  const loadDeptUsers = useCallback(
    (deptId: string) => {
      if (deptUsers.has(deptId) || loadingDeptIds.has(deptId)) return;
      setLoadingDeptIds((prev) => new Set(prev).add(deptId));
      api.org
        .users(deptId)
        .then((list) => {
          setDeptUsers((prev) => new Map(prev).set(deptId, list));
        })
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : "部门成员加载失败");
        })
        .finally(() => {
          setLoadingDeptIds((prev) => {
            const next = new Set(prev);
            next.delete(deptId);
            return next;
          });
        });
    },
    [deptUsers, loadingDeptIds],
  );

  /* ---- 点部门行：设为归属部门 + 展开/收起（展开时按需加载人员） ---- */
  const toggleDept = (deptId: string) => {
    setSelectedDeptId(deptId);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(deptId)) {
        next.delete(deptId);
      } else {
        next.add(deptId);
        loadDeptUsers(deptId);
      }
      return next;
    });
  };

  /* ---- Esc 关闭 ---- */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  /* ---- 派生 ---- */
  const selectedDept = useMemo(
    () => depts.find((d) => d.id === selectedDeptId) ?? null,
    [depts, selectedDeptId],
  );

  const visibleDepts = useMemo(() => {
    const kw = deptFilter.trim();
    if (!kw) return depts;
    return depts.filter((d) => d.name.includes(kw));
  }, [depts, deptFilter]);

  /** 树形缩进深度 */
  const depthOf = useCallback(
    (node: OrgDeptNode): number => {
      let depth = 0;
      let cur: OrgDeptNode | undefined = node;
      const byId = new Map(depts.map((d) => [d.id, d]));
      while (cur?.parentId) {
        cur = byId.get(cur.parentId);
        depth += 1;
        if (depth > 15) break;
      }
      return depth;
    },
    [depts],
  );

  const recurrence: QuickCreateRecurrence = {
    type,
    ...(type === "WEEKLY" ? { weekdays } : {}),
    ...(type === "MONTHLY" ? { monthDays } : {}),
    startDate,
    timeOfDay,
    durationMinutes,
    endType,
    ...(endType === "count" ? { endCount } : { endDate }),
  };

  const labels = planLabels(recurrence);
  const planCount = labels.length;
  // 创建者由后端自动加入受邀并去重，这里做近似预估
  const inviteeCount =
    selected.size > 0
      ? selected.size + 1
      : (deptUsers.get(selectedDeptId ?? "")?.length ?? 0) + 1;

  const formError = (() => {
    if (!selectedDept) return "请点击选择会议归属部门";
    if (!title.trim()) return "请填写会议标题";
    if (title.trim().length > 20) return "标题最多 20 个字符";
    if (type === "WEEKLY" && weekdays.length === 0) return "每周会议至少选择一个星期";
    if (type === "MONTHLY" && monthDays.length === 0) return "每月会议至少选择一个日期";
    if (endType === "count") {
      const cap = type === "MONTHLY" ? 50 : 200;
      if (!endCount || endCount < 1) return "请填写重复场次";
      if (endCount > cap) return `${type === "MONTHLY" ? "每月" : "每周/每工作日"}会议最多重复 ${cap} 场`;
    } else if (!endDate) {
      return "请选择结束日期";
    } else if (endDate < startDate) {
      return "结束日期不能早于开始日期";
    }
    if (inviteeCount > 300) return `单场会议最多邀请 300 人（当前 ${inviteeCount} 人）`;
    return null;
  })();

  /* ---- 交互 ---- */
  const toggleWeekday = (w: number) =>
    setWeekdays((prev) => (prev.includes(w) ? prev.filter((x) => x !== w) : [...prev, w]));

  const toggleMonthDay = (d: number) =>
    setMonthDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  const toggleUser = (userid: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userid)) next.delete(userid);
      else next.add(userid);
      return next;
    });

  /** 主持人必须从已选参会人中选；取消参会人时若主持人被移除则重置 */
  const effectiveHost = hostUserid && selected.has(hostUserid) ? hostUserid : null;

  const handleSubmit = async () => {
    if (formError) {
      setError(formError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // 未选主持人时默认取第一个参会人：会议发起人=主持人，
      // 避免全部会议都由平台管理员发起（企微限制一个发起人同时只能有一场会议）
      const resolvedHost = effectiveHost ?? selectedUsers[0]?.userid ?? null;
      const result = await api.meetings.quickCreate({
        title: title.trim(),
        deptId: selectedDeptId as string,
        memberUserids: [...selected],
        ...(resolvedHost ? { hostUserid: resolvedHost } : {}),
        recurrence,
      });
      const hostName = resolvedHost ? `，主持人：${nameOf.get(resolvedHost) ?? resolvedHost}` : "";
      const mockNote = result.mock ? "（演示模式，未真实创建企微会议）" : "";
      show(`✓ 已为「${result.deptName}」创建 ${result.created} 个周期会议${hostName}${mockNote}`);
      if (result.excessUsers.length > 0) {
        show(`⚠️ ${result.excessUsers.length} 个成员无有效会议账号，未计入受邀`);
      }
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "创建失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  /** 渲染某部门展开后的人员行 */
  const renderDeptMembers = (deptId: string) => {
    const list = deptUsers.get(deptId);
    if (loadingDeptIds.has(deptId) && !list) {
      return (
        <div className="qc-user-loading" style={{ paddingLeft: 30 + depthOf(depts.find((d) => d.id === deptId)!) * 14 }}>
          <span className="spinner" /> 加载成员…
        </div>
      );
    }
    if (!list || list.length === 0) {
      return (
        <div className="qc-user-loading" style={{ paddingLeft: 30 + depthOf(depts.find((d) => d.id === deptId)!) * 14 }}>
          该部门下暂无成员
        </div>
      );
    }
    return list.map((u) => (
      <button
        key={u.userid}
        type="button"
        className={`qc-member qc-member-tree${selected.has(u.userid) ? " checked" : ""}`}
        style={{ paddingLeft: 30 + depthOf(depts.find((d) => d.id === deptId)!) * 14 }}
        onClick={() => toggleUser(u.userid)}
        title={u.userid}
      >
        <span className="qc-user-dot">{selected.has(u.userid) ? "✓" : ""}</span>
        <span className="qc-member-name">👤 {u.name}</span>
        <span className="qc-member-id">{u.userid}</span>
      </button>
    ));
  };

  return (
    <div
      className="modal-mask show"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal qc-modal" role="dialog" aria-modal="true" aria-label="一键建会">
        <div className="modal-head">
          <span>🚀 一键建会 · 周期会议</span>
          <button className="modal-close" onClick={onClose} aria-label="关闭">
            ✕
          </button>
        </div>

        <div className="qc-body">
          {/* ===== 左栏：组织架构树（点部门展开人员，点人员勾选参会） ===== */}
          <aside className="qc-pane-left">
            <div className="qc-pane-title">1. 点击部门展开人员，点击人员选择为参会人</div>
            <input
              className="qc-search"
              type="text"
              placeholder="搜索部门…"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
              aria-label="搜索部门"
            />
            <div className="qc-dept-tree">
              {deptsLoading ? (
                <div className="loading-box">
                  <span className="spinner" />
                  正在加载组织架构…
                </div>
              ) : visibleDepts.length === 0 ? (
                <div className="qc-empty">未找到匹配的部门</div>
              ) : (
                visibleDepts.map((d) => (
                  <div key={d.id}>
                    <button
                      className={`qc-dept-node${d.id === selectedDeptId ? " active" : ""}`}
                      style={{ paddingLeft: 10 + depthOf(d) * 14 }}
                      onClick={() => toggleDept(d.id)}
                      title="点击设为会议归属部门并展开/收起人员"
                    >
                      <span className="qc-caret">{expanded.has(d.id) ? "▾" : "▸"}</span>
                      📁 {d.name}
                      {d.id === selectedDeptId && <span className="qc-dept-tag">归属</span>}
                    </button>
                    {expanded.has(d.id) && renderDeptMembers(d.id)}
                  </div>
                ))
              )}
            </div>
            <div className="qc-member-hint">
              {selected.size > 0
                ? `已选参会人 ${selected.size} 人（跨部门勾选已保留；创建者将自动加入受邀）`
                : "未勾选人员时将邀请归属部门全体成员（创建者自动加入）"}
              {selectedDept ? ` · 归属部门：${selectedDept.name}` : ""}
            </div>
          </aside>

          {/* ===== 右栏：会议配置 ===== */}
          <section className="qc-pane-right">
            <div className="qc-pane-title">2. 配置会议</div>

            <div className="qc-field">
              <label className="qc-label" htmlFor="qc-title">
                会议标题
              </label>
              <input
                id="qc-title"
                type="text"
                placeholder="如：销售一部晨会"
                value={title}
                maxLength={20}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="qc-field">
              <span className="qc-label">会议主持人（从已选参会人中指定，未选时默认第一人）</span>
              {selectedUsers.length === 0 ? (
                <div className="qc-member-hint">请先在左侧点击人员选择参会人，再指定主持人</div>
              ) : (
                <div className="qc-chiprow">
                  {selectedUsers.map((u) => (
                    <button
                      key={u.userid}
                      type="button"
                      className={`qc-chip${effectiveHost === u.userid ? " active" : ""}`}
                      onClick={() => setHostUserid((prev) => (prev === u.userid ? null : u.userid))}
                    >
                      {effectiveHost === u.userid ? "★ " : ""}
                      {u.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="qc-field">
              <span className="qc-label">重复规则</span>
              <div className="tabs" role="tablist" aria-label="重复规则">
                {(
                  [
                    ["DAILY", "每个工作日"],
                    ["WEEKLY", "每周"],
                    ["MONTHLY", "每月"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={type === value}
                    className={`tab${type === value ? " active" : ""}`}
                    onClick={() => setType(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {type === "WEEKLY" && (
              <div className="qc-field">
                <span className="qc-label">
                  重复星期（可多选，每个星期创建一个会议系列）
                </span>
                <div className="qc-chiprow">
                  {WEEKDAY_ORDER.map((w) => (
                    <button
                      key={w}
                      type="button"
                      className={`qc-chip${weekdays.includes(w) ? " active" : ""}`}
                      onClick={() => toggleWeekday(w)}
                    >
                      周{WEEKDAY_CN[w % 7]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {type === "MONTHLY" && (
              <div className="qc-field">
                <span className="qc-label">
                  重复日期（可多选，每个日期创建一个会议系列）
                </span>
                <div className="qc-daygrid">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={`qc-chip qc-chip-day${monthDays.includes(d) ? " active" : ""}`}
                      onClick={() => toggleMonthDay(d)}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="qc-field-row">
              <div className="qc-field">
                <label className="qc-label" htmlFor="qc-start">
                  开始日期
                </label>
                <input id="qc-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div className="qc-field">
                <label className="qc-label" htmlFor="qc-time">
                  开始时间
                </label>
                <input id="qc-time" type="time" value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)} />
              </div>
              <div className="qc-field">
                <label className="qc-label" htmlFor="qc-duration">
                  时长
                </label>
                <select
                  id="qc-duration"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(Number(e.target.value))}
                >
                  {DURATION_OPTIONS.map((m) => (
                    <option key={m} value={m}>
                      {m} 分钟
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="qc-field">
              <span className="qc-label">结束方式</span>
              <div className="qc-chiprow">
                <button
                  type="button"
                  className={`qc-chip${endType === "count" ? " active" : ""}`}
                  onClick={() => setEndType("count")}
                >
                  按次数
                </button>
                <button
                  type="button"
                  className={`qc-chip${endType === "date" ? " active" : ""}`}
                  onClick={() => setEndType("date")}
                >
                  按日期
                </button>
              </div>
            </div>

            {endType === "count" ? (
              <div className="qc-field">
                <label className="qc-label" htmlFor="qc-count">
                  重复场次（{type === "MONTHLY" ? "每月系列最多 50 场" : "每周/每工作日最多 200 场"}）
                </label>
                <input
                  id="qc-count"
                  type="number"
                  min={1}
                  max={type === "MONTHLY" ? 50 : 200}
                  value={endCount}
                  onChange={(e) => setEndCount(Number(e.target.value))}
                />
              </div>
            ) : (
              <div className="qc-field">
                <label className="qc-label" htmlFor="qc-enddate">
                  结束日期（含当日）
                </label>
                <input id="qc-enddate" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            )}

            <div className="qc-summary">
              <div>
                📋 将创建 <b>{planCount}</b> 个周期会议：
                {labels.join("、")} {timeOfDay}，每次 {durationMinutes} 分钟
              </div>
              <div className="qc-summary-sub">
                {selectedDept ? `部门：${selectedDept.name} · ` : ""}
                受邀 {inviteeCount} 人
                {effectiveHost ? ` · 主持人：${nameOf.get(effectiveHost) ?? effectiveHost}` : " · 未指定主持人"} · 自{" "}
                {startDate} 起
              </div>
            </div>

            {error && <div className="qc-error">{error}</div>}

            <div className="qc-actions">
              <button type="button" className="btn" onClick={onClose} disabled={submitting}>
                取消
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void handleSubmit()}
                disabled={submitting || formError !== null}
              >
                {submitting ? "创建中…" : `创建 ${planCount} 个周期会议`}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
